import {
  DatabaseAuto, IdataArrayResponse, IfilterProps,
  IunreliableVendor
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Represents a vendor that has been flagged as unreliable in the system.
 * This class provides methods to manage and query vendors that have been marked
 * as unreliable, either with warnings or on a red list.
 *
 * @extends DatabaseAuto - Extends the base DatabaseAuto class for common database operations
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UnreliableVendor} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const unreliableVendor = await UnreliableVendor.getAll();
 *
 * const unreliableVendor = await UnreliableVendor.filterAll({
 *
 * });
 *
 * const unreliableVendor = await UnreliableVendor.bulkDelete({
 *
 * });
 *
 * const unreliableVendor = await UnreliableVendor.getOne('resourceId');
 *
 * const unreliableVendor = await UnreliableVendor.remove();
 *
 * ```
 */
export class UnreliableVendor extends DatabaseAuto {
  /** Base API endpoint for unreliable vendor operations */
  private static readonly baseUrl = '/un-reliable-vendor';

  /** Array of companies that have been flagged as unreliable */
  readonly flaggedCompanies: IunreliableVendor['flaggedCompanies'];

  /**
   * Creates a new instance of UnreliableVendor.
   * @param {IunreliableVendor} data - The data used to initialize the unreliable vendor record
   * @throws {Error} Throws an error if no data is provided
   */
  constructor(data: IunreliableVendor) {
    super(data);
    this.flaggedCompanies = data.flaggedCompanies;
  }

  /**
   * Retrieves all unreliable vendor records with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: UnreliableVendor[] }>}
   * An object containing the total count and an array of vendor records
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IunreliableVendor>>(`${UnreliableVendor.baseUrl}?${query}`);
    const unreliableVendors = await lastValueFrom(observer$);

    return {
      count: unreliableVendors.count,
      dataArr: unreliableVendors.data.map((val) => new UnreliableVendor(val))
    };
  }

  /**
   * Filters unreliable vendor records based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: UnreliableVendor[] }>}
   * An object containing the total count and an array of filtered vendor records
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IunreliableVendor>>(`${UnreliableVendor.baseUrl}/filter`, filter);
    const unreliableVendors = await lastValueFrom(observer$);

    return {
      count: unreliableVendors.count,
      dataArr: unreliableVendors.data.map((val) => new UnreliableVendor(val))
    };
  }

  /**
   * Retrieves a single unreliable vendor record by its ID.
   * @param {string} resourceId - The unique identifier of the vendor record to retrieve
   * @returns {Promise<UnreliableVendor>} The requested vendor record
   * @throws {Error} If the record is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IunreliableVendor>(`${UnreliableVendor.baseUrl}/${resourceId}`);
    const unreliableVendor = await lastValueFrom(observer$);

    return new UnreliableVendor(unreliableVendor);
  }

  /**
   * Modifies the unreliable vendor list by adding or removing a company.
   * @param {string} companyId - The ID of the company to modify in the list
   * @param {'red' | 'warn'} listType - The type of list to modify ('red' for red list, 'warn' for warning list)
   * @param {'add' | 'remove'} action - The action to perform (add or remove from the list)
   * @param {number} [warnCount] - Optional warning count when adding to the warning list
   * @returns {Promise<UnreliableVendor>} The updated unreliable vendor record
   * @throws {Error} If the operation fails or the company is not found
   */
  static async modify(companyId: string, listType: 'red' | 'warn', action: 'add' | 'remove', warnCount?: number) {
    const observer$ = CommerceClient.ehttp
      .makePut<IunreliableVendor>(`${UnreliableVendor.baseUrl}`, { companyId, listType, action, warnCount });

    return new UnreliableVendor(await lastValueFrom(observer$));
  }
}
